import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, LineReveal } from "./motion/Reveal";

const CHAPTERS = [
  { n: "01", title: "Intentionality", text: "Every decision, from the typeface to the transition, exists for a reason. We design around your business and the people you want to reach, never around a template." },
  { n: "02", title: "Digital Architecture", text: "Structure is invisible when it works. We plan how visitors move, what they notice first, and where they naturally arrive, so exploring feels effortless." },
  { n: "03", title: "Lasting Impact", text: "A website should earn trust and stay in memory. We combine thoughtful design, modern technology, careful customization and a genuine focus on your satisfaction." },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1]);

  return (
    <section id="about" data-testid="about-section" ref={ref} className="section-y bg-cream relative">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal><p className="eyebrow mb-6">About</p></Reveal>
            <LineReveal inView as="h2" lines={["What is", "Amara Studios?"]} className="h2" />
            <Reveal delay={0.2} className="mt-10 space-y-6 body-lg">
              <p>Amara Studios is a Bangalore-based web design studio created to make high-quality websites more accessible to businesses.</p>
              <p>We create websites that do more than simply exist online. We design digital experiences that help businesses connect with their customers, establish credibility, expand their reach, and create a memorable first impression.</p>
              <p>Our approach combines thoughtful design, modern technology, customization, and a strong focus on customer satisfaction.</p>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:pl-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              <Reveal className="md:col-span-7 md:mt-20 relative">
                <div className="relative overflow-hidden aspect-[4/5] bg-sand">
                  <motion.img
                    style={{ y, scale }}
                    src="https://images.unsplash.com/photo-1601993957728-1e56ab70c5a8?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200"
                    alt="Warm minimalist interior with natural light, reflecting the calm aesthetic of Amara Studios"
                    className="absolute inset-0 h-[116%] w-full object-cover -top-[8%]"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -bottom-6 -left-4 sm:-left-8 glass border border-taupe-line px-5 py-4">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-sage">Studio</p>
                  <p className="font-serif text-xl text-charcoal">Bangalore, India</p>
                </div>
              </Reveal>
              <div className="md:col-span-5 flex flex-col justify-between gap-8">
                <Reveal delay={0.1} className="border-t border-charcoal/20 pt-6">
                  <p className="font-serif text-6xl lg:text-7xl font-light text-charcoal leading-none">100<span className="text-sage">%</span></p>
                  <p className="text-sm text-ink/70 mt-2 font-light">Ownership of your website, code and domain transferred to you on completion.</p>
                </Reveal>
                <Reveal delay={0.2} className="border-t border-charcoal/20 pt-6">
                  <p className="font-serif text-4xl lg:text-5xl font-light italic text-charcoal leading-none">Made to be explored.</p>
                  <p className="text-sm text-ink/70 mt-3 font-light">Not just an online business card. An experience that captures attention and builds trust.</p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-28 lg:mt-40 grid grid-cols-1 md:grid-cols-3 gap-px bg-taupe-line border border-taupe-line">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12} data-testid={`manifesto-chapter-${i + 1}`} className="group bg-cream hover:bg-ivory transition-colors duration-500 p-8 lg:p-10 relative overflow-hidden">
              <span className="font-mono text-[11px] text-sage tracking-[0.25em]">{c.n}</span>
              <h3 className="font-serif text-3xl lg:text-4xl font-light text-charcoal mt-10">{c.title}</h3>
              <p className="text-sm sm:text-[15px] text-ink/70 leading-relaxed mt-5 font-light">{c.text}</p>
              <span className="absolute bottom-0 left-0 h-px w-0 bg-sage transition-[width] duration-700 ease-out group-hover:w-full" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
